/**
 * PlotDataContext — shares full and filtered dataset between
 * the DataExplorer (AG Grid) and the PlotBuilder.
 *
 * The DataExplorer pushes filtered rows whenever its filter changes.
 * The PlotBuilder reads either `fullData` or `filteredData` depending
 * on the user's data-source selection.
 */

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';

interface PlotDataContextValue {
  /** The complete, unfiltered dataset */
  fullData: Record<string, unknown>[];
  /** The currently displayed (filtered) rows from AG Grid */
  filteredData: Record<string, unknown>[];
  /** Call this to update the full dataset (e.g. on initial load) */
  setFullData: (data: Record<string, unknown>[]) => void;
  /** Call this whenever the AG Grid filter changes */
  setFilteredData: (data: Record<string, unknown>[]) => void;
}

const PlotDataCtx = createContext<PlotDataContextValue>({
  fullData: [],
  filteredData: [],
  setFullData: () => {},
  setFilteredData: () => {},
});

export function PlotDataProvider({ children }: { children: ReactNode }) {
  const [fullData, setFullDataState] = useState<Record<string, unknown>[]>([]);
  const [filteredData, setFilteredDataState] = useState<Record<string, unknown>[]>([]);

  const setFullData = useCallback((data: Record<string, unknown>[]) => {
    setFullDataState(data);
    setFilteredDataState(data); // initially, filtered = full
  }, []);

  const setFilteredData = useCallback((data: Record<string, unknown>[]) => {
    setFilteredDataState(data);
  }, []);

  const value = useMemo(() => ({
    fullData, filteredData, setFullData, setFilteredData,
  }), [fullData, filteredData, setFullData, setFilteredData]);

  return (
    <PlotDataCtx.Provider value={value}>
      {children}
    </PlotDataCtx.Provider>
  );
}

export function usePlotData() {
  return useContext(PlotDataCtx);
}
